export const categories = [
  'analytics',
  'marketing',
  'advertising',
  'functionality',
  'required',
] as const

export const defaultCookieNames: ConsentCookieNameMap = {
  acknowledged: '_cookies_banner_dismissed',
  functionality: ['_cookie_functionality_storage_accepted'],
  // We don't actually set a cookie for 'required', as it's always enabled
  required: [],
  analytics: ['_cookie_analytics_accepted'],
  marketing: ['_cookie_marketing_accepted'],
  advertising: [
    '_cookie_advertising_accepted',
    '_cookie_ad_user_data_accepted',
    '_cookie_ad_personalization_accepted',
  ],
}

const ONE_YEAR = 31536000

export const defaultOptions: Required<ConsentCookieOptions> = {
  domain: '.monzo.com',
  path: '/',
  maxAge: ONE_YEAR,
  secure: true,
  sameSite: 'Strict',
  cookieNames: defaultCookieNames,
  respectGlobalPrivacyControl: true,
}

/** The names of the categories of consent */
export type ConsentCategory = (typeof categories)[number]

/** A configuration object mapping consent categories to cookie names */
export type ConsentCategoryCookieNameMap = Record<ConsentCategory, string[]>

/** A configuration object which specifies names for cookies */
export type ConsentCookieNameMap = ConsentCategoryCookieNameMap & {
  acknowledged: string
}

export interface ConsentCookieOptions {
  /**
   * Allows overriding of the cookie names used for each category of consent.
   * The cookie names are supplied as _arrays_ of strings, to allow for multiple
   * named cookies within a given category (as per our mapping onto GTM cookie)
   * names.
   *
   * For example, the advertising category sets three cookies:
   * - `_cookie_advertising_accepted`
   * - `_cookie_ad_user_data_accepted`
   * - `_cookie_ad_personalization_accepted`
   */
  cookieNames?: ConsentCookieNameMap

  /**
   * If enabled (default), the `navigator.globalPrivacyControl` (GPC) property
   * will be respected in the browser.
   *
   * It's unlikely that you'll want to disable this in your app, as it's a
   * privacy feature that allows users to control their data usage across the
   * web.
   *
   * Consent that has already been granted via cookies will be honoured, but
   * new users will not be prompted for consent if they have enabled GPC.
   *
   * @see {@link https://globalprivacycontrol.org/}
   */
  respectGlobalPrivacyControl?: boolean

  /**
   * Sets the domain for the cookie. Be sure to include the leading dot to
   * allow subdomains to read the cookie.
   * @default '.monzo.com'
   */
  domain?: string
  /**
   * Sets the path that the cookie applies to.
   * @default '/'
   */
  path?: string
  /**
   * Sets the maximum age of the cookie in seconds.
   * @default ONE_YEAR
   */
  maxAge?: number
  /**
   * Sets whether the cookie should only be sent over HTTPS.
   * @default true
   */
  secure?: boolean
  /**
   * Sets whether the cookie should be sent with cross-site requests.
   * @default 'Strict'
   */
  sameSite?: 'Strict' | 'Lax' | 'None'
}
